/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDDateRange from 'components/MDDateRange';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: 'id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'DATE',
  branch: 'ALL',
  status: 'ALL',
  payment: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initSummary = {
  quantity: '',
  amount: '',
  vat_amount: '',
  total_discount: '',
  total_discount_approve: '',
  total: '',
};

function RpSaleOrderList() {
  const mod = 'rp_saleorder_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboStatus, setCboStatus] = useState([]);
  const [cboPayment, setCboPayment] = useState([]);
  const [summary, setSummary] = useState(initSummary);
  const [urlExcel, setUrlExcel] = useState('');
  const [urlExpress, setUrlExpress] = useState('');
  const [buttonGroup, setButtonGroup] = useState(null);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'id', label: t(`attr.image`), align: 'center', width: 40 },
    { id: 'name', label: 'สินค้า', align: 'left' },
    { id: 'code', label: 'เลขที่ใบเสร็จ', align: 'center' },
    { id: 'created_at', label: 'วันที่', align: 'center' },
    { id: 'branch_id', label: 'สาขา', align: 'center' },
    { id: 'quantity', label: 'จำนวน', align: 'right' },
    { id: 'cost', label: 'ทุน', align: 'right' },
    { id: 'price', label: 'ราคา', align: 'right' },
    { id: 'amount', label: 'มูลค่า', align: 'right' },
    { id: 'vat_amount', label: 'ภาษี', align: 'right' },
    { id: 'final_discount', label: 'ส่วนลด', align: 'right' },
    { id: 'final_discount_approve', label: 'ส่วนลดที่อนุมัติ', align: 'right' },
    { id: 'total', label: 'รวม', align: 'right' },
    { id: 'payment_name', label: 'การชำระ', align: 'left' },
    { id: 'client_name', label: 'ลูกค้า', align: 'left' },
    { id: 'ar_code', label: 'AR', align: 'left' },
    { id: 'campaign_id', label: 'โปรโมชั่น', align: 'left' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    setSummary(initSummary);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
      status: data.status,
      payment: data.payment,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);

        setSummary(result.summary);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    setButtonGroup(null);

    let param = `download`;
    param += `?search=${find.search}`;
    param += `&branch=${find.branch}`;
    param += `&period=${find.period}`;
    param += `&payment=${find.payment}`;
    param += find.period === 'ALL' ? '' : `&date_start=${find.date_start}`;
    param += find.period === 'ALL' ? '' : `&date_stop=${find.date_stop}`;

    window.open(`${urlExcel}/${param}`, '_self');
    // window.open(`${urlExcel}/${param}`, "_blank");
  };

  const express = () => {
    setButtonGroup(null);

    let param = `download`;
    param += `?search=${find.search}`;
    param += `&branch=${find.branch}`;
    param += `&period=${find.period}`;
    param += find.period === 'ALL' ? '' : `&date_start=${find.date_start}`;
    param += find.period === 'ALL' ? '' : `&date_stop=${find.date_stop}`;

    window.open(`${urlExpress}/${param}`, '_self');
    // window.open(`${urlExpress}/${param}`, "_blank");
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setCboStatus(result.stepper);
        setUrlExcel(result.excel);
        setUrlExpress(result.express);
        setCboPayment(result.payment);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <MDInput
              fullWidth
              value={find.status}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>สถานะ :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboStatus.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <MDInput
              fullWidth
              value={find.payment}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, payment: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>การชำระ :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboPayment.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={6}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
          {find.branch === 'ALL' &&
          Math.abs(new Date(find.date_start) - new Date(find.date_stop)) / (1000 * 60 * 60 * 24) >
            31 ? null : (
            <Grid item xs={12} sm={12} md={3} lg={2}>
              <MDBox display='flex' justifyContent='end' gap={1}>
                <MDButton
                  variant='gradient'
                  color='success'
                  onClick={(e) => setButtonGroup(e.currentTarget)}
                >
                  <Icon>file_download</Icon>
                  &nbsp;Excel
                </MDButton>
                <Menu
                  sx={{ mt: '45px' }}
                  id='menu-appbar'
                  anchorEl={buttonGroup}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(buttonGroup)}
                  onClose={() => setButtonGroup(null)}
                >
                  <MenuItem onClick={excel}>
                    <MDTypography variant='body2'>
                      <i className='far fa-file-excel' />
                      &nbsp;Excel
                    </MDTypography>
                  </MenuItem>
                  <MenuItem onClick={express}>
                    <MDTypography variant='body2'>
                      <i className='far fa-file-excel' />
                      &nbsp;Express
                    </MDTypography>
                  </MenuItem>
                </Menu>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_pos'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{Func.imageDisplay(row)}</TableCell>
                      <TableCell align='left'>
                        <MDBox className='nowrap'>
                          {row.code} <span className='color-default'>{row.sku}</span>
                        </MDBox>
                        <MDBox className='cut' width={200}>
                          {row.name}
                        </MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox className='nowrap'>{row.so_code}</MDBox>
                        <MDBox className='nowrap'>{Func.statusFaDisplay(row)}</MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox className='nowrap' align='center'>
                          {Func.dateDisplay(row.created_at)}
                        </MDBox>
                        <MDBox className='nowrap' align='center'>
                          {Func.timeDisplay(row.created_at)}
                        </MDBox>
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.branch_name}
                      </TableCell>
                      <TableCell align='right' className='nowrap highlight-info'>
                        <MDBox className='nowrap'>{Func.numberDisplay(row.quantity)}</MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        <MDBox className='nowrap'>{row.cost}</MDBox>
                        <MDBox className='nowrap'>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        <MDBox className='nowrap'>{row.price}</MDBox>
                        <MDBox className='nowrap'>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='nowrap'>{Func.numberDisplay(row.amount, 2)}</MDBox>
                        <MDBox className='nowrap'>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='nowrap'>{Func.numberDisplay(row.vat_amount, 2)}</MDBox>
                        <MDBox className='nowrap'>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='nowrap'>
                          {Func.numberDisplay(row.final_discount, 2)}
                        </MDBox>
                        <MDBox className='nowrap'>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='nowrap'>
                          {Func.numberDisplay(row.final_discount_approve, 2)}
                        </MDBox>
                        <MDBox className='nowrap'>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right' className='nowrap highlight-success'>
                        <MDBox className='nowrap'>{Func.numberDisplay(row.total, 2)}</MDBox>
                        <MDBox className='nowrap'>บาท</MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox className='nowrap'>{row.payment_name}</MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox className='nowrap'>{row.client_name}</MDBox>
                        <MDBox className='nowrap'>{row.country_name}</MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox className='nowrap'>{row.ar_code}</MDBox>
                        <MDBox className='nowrap'>{row.ar_name}</MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox className='nowrap'>{row.campaign_code}</MDBox>
                        <MDBox className='nowrap'>{row.campaign_name}</MDBox>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell align='right' colSpan={5}>
                      รวม
                    </TableCell>
                    <TableCell align='right' className='highlight-info'>
                      {Func.numberDisplay(summary.quantity)}
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell align='right'>{Func.numberDisplay(summary.amount)}</TableCell>
                    <TableCell align='right'>{Func.numberDisplay(summary.vat_amount)}</TableCell>
                    <TableCell align='right'>
                      {Func.numberDisplay(summary.total_discount)}
                    </TableCell>
                    <TableCell align='right'>
                      {Func.numberDisplay(summary.total_discount_approve)}
                    </TableCell>
                    <TableCell align='right' className='highlight-success'>
                      {Func.numberDisplay(summary.total)}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_pos`), t(`menu.rp_saleorder_list`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpSaleOrderList;
